import { IconBrandX } from '@tabler/icons-react';
import { ArrowRight, ChevronRightIcon, Mail } from 'lucide-react';
import * as React from 'react';

import { CardHoverableGridPattern } from '@/components/card/card.hoverable-grid-pattern';
import { HeroPatternNew } from '@/components/hero-pattern';
import { DiscordIcon, GithubIcon } from '@/components/svgs/icons';
import { Button } from '@/components/ui/button';

import { CalendlyWidget } from '@/lib/calendly/calendly.widget';
import {
  Navigation,
  SectionFooter,
  SectionReadyToRelease,
  SignInOrDashboard,
} from '@/pages/unauthenticated/components';

import { faqs } from './components/constants';
import { SupportFaqs } from './components/support.faqs';

export default function SupportPage() {
  return (
    <>
      <Navigation>
        <SignInOrDashboard />
      </Navigation>
      <main>
        <HeroPatternNew className="isolate lg:top-132" />

        <section className="redesign-container">
          <div className="mx-auto max-w-7xl px-0 py-24 sm:py-32 lg:flex lg:flex-col xl:flex-row lg:items-center lg:gap-x-10 lg:px-8 lg:py-40">
            <div className="mx-auto max-w-xl xl:mx-0 lg:flex-auto">
              <div className="flex">
                <div className="relative flex items-center gap-x-4 rounded-full bg-halfmast-1000 px-4 py-1 text-sm/6 text-muted-foreground ring-1 ring-gray-900/10 hover:ring-gray-900/20">
                  <span className="font-semibold text-primary">
                    What&#39;s new
                  </span>
                  <span
                    aria-hidden="true"
                    className="h-4 w-px bg-halfmast-800"
                  />
                  <a
                    href="https://github.com/flagsync/cli"
                    className="flex items-center gap-x-1"
                  >
                    <span aria-hidden="true" className="absolute inset-0" />
                    Just shipped FlagSync CLI
                    <ChevronRightIcon
                      aria-hidden="true"
                      className="-mr-2 size-5 text-halfmast-800"
                    />
                  </a>
                </div>
              </div>
              <h1 className="mt-10 text-5xl font-black tracking-tight text-pretty sm:text-7xl max-w-[10ch]">
                Drop us a{' '}
                <span className="text-primary italic relative">line</span>
              </h1>
              <p className="mt-8 text-lg font-medium text-pretty text-muted-foreground sm:text-xl/8">
                Got questions? Feedback? Bugs? We’re listening. Check the docs
                or reach out directly.
              </p>

              <div className="mt-10">
                <Button
                  asChild
                  variant="glowing"
                  className="uppercase w-full md:w-fit flex-shrink-0"
                >
                  <a
                    href="https://docs.flagsync.com"
                    className="inline-flex items-center"
                  >
                    Documentation
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </a>
                </Button>
              </div>
            </div>
            <div className="mt-16 sm:mt-24 xl:mt-0 lg:shrink-0">
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:gap-8">
                <div className="rounded-lg">
                  <CardHoverableGridPattern
                    index={2}
                    className="bg-background rounded-lg p-10 pt-2"
                  >
                    <h3 className="text-base/7 font-semibold inline-flex items-center gap-2">
                      <Mail
                        aria-hidden="true"
                        className="size-8 text-primary"
                      />
                      Email
                    </h3>
                    <dl className="mt-3 space-y-1 text-sm/6 text-primary">
                      <div>
                        <dt className="sr-only">Email</dt>
                        <dd>
                          <a
                            href="mailto:support@flagsync.com"
                            className="font-semibold text-primary"
                          >
                            support@flagsync.com
                          </a>
                        </dd>
                      </div>
                    </dl>
                  </CardHoverableGridPattern>
                </div>
                <div className="rounded-lg">
                  <CardHoverableGridPattern
                    index={2}
                    className="bg-background rounded-lg p-10 pt-2"
                  >
                    <h3 className="text-base/7 font-semibold inline-flex items-center gap-2">
                      <GithubIcon
                        aria-hidden="true"
                        fill="#1CEFA5"
                        className="size-8 lg:size-10 text-primary"
                      />
                      Github
                    </h3>
                    <dl className="mt-3 space-y-1 text-sm/6 text-primary">
                      <div>
                        <dt className="sr-only">Github</dt>
                        <dd>
                          <a
                            href="https://github.com/flagsync"
                            className="font-semibold text-primary"
                          >
                            https://github.com/flagsync
                          </a>
                        </dd>
                      </div>
                    </dl>
                  </CardHoverableGridPattern>
                </div>
                <div>
                  <CardHoverableGridPattern
                    index={2}
                    className="bg-background p-10 pt-2"
                  >
                    <h3 className="text-base/7 font-semibold inline-flex items-center gap-2">
                      <DiscordIcon
                        aria-hidden="true"
                        fill="#1CEFA5"
                        className="size-8 text-primary"
                      />
                      Discord
                    </h3>
                    <dl className="mt-3 space-y-1 text-sm/6 text-primary">
                      <div>
                        <dt className="sr-only">Discord</dt>
                        <dd>
                          <a
                            href="https://discord.gg/4ggZrzzUJs"
                            className="font-semibold text-primary"
                          >
                            https://discord.gg/4ggZrzzUJs
                          </a>
                        </dd>
                      </div>
                    </dl>
                  </CardHoverableGridPattern>
                </div>
                <div className="rounded-lg">
                  <CardHoverableGridPattern
                    index={2}
                    className="bg-background rounded-lg p-10 pt-2"
                  >
                    <h3 className="text-base/7 font-semibold inline-flex items-center gap-2">
                      <IconBrandX
                        aria-hidden="true"
                        className="size-8 text-primary"
                      />
                      Twitter
                    </h3>
                    <dl className="mt-3 space-y-1 text-sm/6 text-primary">
                      <div>
                        <dt className="sr-only">Twitter</dt>
                        <dd>
                          <a
                            href="https://twitter.com/flagsync"
                            className="font-semibold text-primary"
                          >
                            https://twitter.com/flagsync
                          </a>
                        </dd>
                      </div>
                    </dl>
                  </CardHoverableGridPattern>
                </div>
              </div>
            </div>
          </div>
        </section>
        <SupportFaqs faqs={faqs} />
        <SectionReadyToRelease />
        <SectionFooter />
        <CalendlyWidget />
      </main>
    </>
  );
}
